import { useEffect, useState } from 'react';
import { archiveByKey, type Archive } from '../../api/archives';
import { timelineStripTileUrl, TIMELINE_TILE_SECONDS } from '../../engine/mediaurls';
import { getPartOffsets } from '../../engine/partoffsets';
import type { AudioClip, Source, VideoClip } from '../../format/types';
import { useTimelineCtx } from './context';

// --- part-offset cache (concatenated time for multi-part items) -------------
// Shared promise cache lives in engine/partoffsets.ts (also used by the preview
// engine's frame-fallback path).

export function usePartOffsets(site: string, item: string, needed: boolean): number[] | null {
  const [offsets, setOffsets] = useState<number[] | null>(null);
  useEffect(() => {
    if (!needed) return;
    let alive = true;
    getPartOffsets(site, item).then(
      (o) => alive && setOffsets(o),
      () => alive && setOffsets([]),
    );
    return () => {
      alive = false;
    };
  }, [site, item, needed]);
  return needed ? offsets : [];
}

export function sourceTitle(src: Source | undefined, sourceId: string): string {
  if (!src) return sourceId;
  return src.kind === 'pandora-video' ? src.title || src.item : 'image';
}

/**
 * Pandora timeline-strip background for a media clip. Strips are 1px/second
 * of concatenated item time, tiled every 3600s; we lay the tiles in a hidden
 * overflow row scaled to pps/speed and offset by (partOffset + in).
 */
export function ClipStrip({
  clip,
  src,
  archives,
  audio,
}: {
  clip: VideoClip | AudioClip;
  src: Source | undefined;
  archives: Archive[];
  audio: boolean;
}) {
  const { pps } = useTimelineCtx();
  const isPandora = src?.kind === 'pandora-video';
  const archive = isPandora ? archiveByKey(archives, src.archive) : undefined;
  const needOffsets = Boolean(isPandora && src.part > 1 && archive);
  const offsets = usePartOffsets(archive?.site ?? '', isPandora ? src.item : '', needOffsets);

  if (!isPandora || !archive) return null;
  if (needOffsets && offsets === null) return null; // still loading part offsets

  const partOffset = needOffsets ? (offsets?.[src.part - 1] ?? 0) : 0;
  const speed = clip.speed ?? 1;
  const scale = pps / speed; // px per source-second
  const totalItemDur = partOffset + src.duration; // enough tiles to cover this part
  const tiles = Math.max(1, Math.ceil(totalItemDur / TIMELINE_TILE_SECONDS));
  const marginLeft = -(partOffset + clip.in) * scale;

  const band = (variant: 'timeline' | 'timelineaudio', className: string) => {
    const imgs: JSX.Element[] = [];
    for (let i = 0; i < tiles; i++) {
      const tileDur = Math.min(TIMELINE_TILE_SECONDS, totalItemDur - i * TIMELINE_TILE_SECONDS);
      imgs.push(
        <img
          key={i}
          src={timelineStripTileUrl(archive, src.item, i, variant)}
          alt=""
          draggable={false}
          style={{ width: tileDur * scale }}
        />,
      );
    }
    return (
      <div className={className} aria-hidden>
        <div className="tl-clip-strip-row" style={{ marginLeft }}>
          {imgs}
        </div>
      </div>
    );
  };

  // Audio clips: the waveform fills the strip (unchanged). Video clips: the
  // thumbnail strip fills the block with a short waveform band pinned to the
  // bottom, so you can see the audio you're cutting on.
  if (audio) return band('timelineaudio', 'tl-clip-strip');
  return (
    <>
      {band('timeline', 'tl-clip-strip')}
      {band('timelineaudio', 'tl-clip-strip tl-clip-wave')}
    </>
  );
}
